import React, { useEffect, useMemo, useState } from "react";
import { FlightSummary } from "@/components/FlightInfoFields";
import { useParams } from "react-router-dom";
import { publicApi, API } from "@/lib/api";
import { toast } from "sonner";
import { PaymentForm, CreditCard, ApplePay, GooglePay } from "react-square-web-payments-sdk";
import { CreditCard as CardIcon, Smartphone, Banknote, Check, ShieldCheck, ArrowRight, Download } from "lucide-react";
import { normalizeTripDetails, fmtStopTime, fmtBookedOn } from "@/lib/tripDetails";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric",
      year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function shortAddr(a = "") {
  return (a || "").split(",")[0] || a || "—";
}

export default function PayInvoice() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "card" | "pending" | null
  const [savedCards, setSavedCards] = useState([]);  // [{saved_card_id, brand, last4}]

  const reload = async () => {
    try {
      const r = await publicApi.get(`/invoices/consolidated/public/${token}`);
      setInv(r.data);
      if (r.data.status === "paid") setDone("card");
      else if (r.data.status === "pending_review") setDone("pending");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Invoice not found or link expired.");
    }
  };

  const loadSavedCards = async () => {
    try {
      const r = await publicApi.get(`/invoices/consolidated/public/${token}/saved-cards`);
      setSavedCards(Array.isArray(r.data) ? r.data : []);
    } catch {
      setSavedCards([]);
    }
  };

  useEffect(() => { reload(); loadSavedCards(); /* eslint-disable-next-line */ }, [token]);

  // One-tap UX: when the customer lands from the PDF QR code / SMS link,
  // pre-open the Card panel so Apple Pay + Google Pay buttons are already
  // visible — no extra tap on the method picker required.
  useEffect(() => {
    if (!inv || picked) return;
    if (inv.status === "paid" || done) return;
    if (inv.payment_methods?.card?.enabled) setPicked("card");
  }, [inv, picked, done]);

  const baseTotal = Number(inv?.total || 0);
  // The payable amount is FIXED to the invoice total — customers cannot edit
  // it (per dispatcher request). tip_amount stays 0 for invoice payments.
  const payAmount = baseTotal;
  const tipAmount = 0;
  const amount = useMemo(() => payAmount.toFixed(2), [payAmount]);

  if (err) {
    return (
      <Shell>
        <div className="max-w-md mx-auto text-center mt-24 px-6">
          <h1 className="font-serif text-3xl text-[#D4AF37] mb-3">Invoice unavailable</h1>
          <p className="text-sm text-[#A1A1AA]">{err}</p>
        </div>
      </Shell>
    );
  }
  if (!inv) {
    return (
      <Shell>
        <div className="text-center mt-24 text-sm text-[#A1A1AA]">Loading your invoice…</div>
      </Shell>
    );
  }

  const handleSquareToken = async (tk) => {
    setBusy(true);
    try {
      const r = await publicApi.post(`/invoices/consolidated/public/${token}/pay-card`, {
        source_id: tk.token,
        verification_token: tk.verificationToken || null,
        tip_amount: tipAmount,
      });
      const sc = r.data?.saved_card;
      toast.success(sc && !sc.already_on_file
        ? `Payment complete — ${sc.brand || "card"} ••••${sc.last4} saved to your profile for next time.`
        : "Payment complete — thank you!");
      setDone("card");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  const chargeSavedCard = async (savedCardId) => {
    if (busy) return;
    setBusy(true);
    try {
      await publicApi.post(`/invoices/consolidated/public/${token}/charge-saved`, {
        saved_card_id: savedCardId,
        tip_amount: tipAmount,
      });
      toast.success("Paid in one tap — thank you!");
      setDone("card");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  const handleManual = async (method, reference = null) => {
    setBusy(true);
    try {
      await publicApi.post(`/invoices/consolidated/public/${token}/notify-manual`, {
        method, reference,
      });
      toast.success("Thank you — we'll confirm receipt shortly.");
      setDone("pending");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record");
    } finally {
      setBusy(false);
    }
  };

  if (done === "card" || inv.status === "paid") {
    return (
      <Shell>
        <PaidBanner inv={inv} token={token}/>
        <InvoiceSummary inv={inv}/>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-10 pb-24" data-testid="pay-invoice-page">
        {/* Editorial hero — left-aligned, monospace invoice #, oversized amount */}
        <div className="mb-12 pb-10 border-b border-black/10">
          <div className="overline mb-2">{inv.kind === "end_of_day" ? "End-of-Day Invoice" : "Invoice"} <span className="font-mono normal-case tracking-normal text-[#050505]">{inv.invoice_number}</span></div>
          {inv.kind === "end_of_day" && inv.service_date && (
            <div className="text-[#52525b] text-xs mb-1 uppercase tracking-[0.18em]" data-testid="eod-service-date">Service date · {fmtBookedOn(inv.service_date)}</div>
          )}
          <div className="text-[#52525b] text-sm mb-1">
            {(() => {
              const routeTrips = Array.isArray(inv.trip_details) ? inv.trip_details.length : (inv.trip_details ? 1 : 0);
              const n = inv.trip_count || routeTrips;
              return n > 0 ? `${n} trip${n === 1 ? "" : "s"} · ` : "";
            })()}billed to <span className="text-[#050505] font-medium">{inv.customer_name || inv.customer_email}</span>
          </div>
          <div className="flex items-end justify-between flex-wrap gap-6 mt-6">
            <div className="flex-1 min-w-[240px]">
              <div className="overline mb-2">Total due</div>
              <div className="flex items-baseline gap-1 font-[Outfit] text-6xl md:text-7xl tracking-tighter font-light leading-none">
                <span className="text-[#71717A]">$</span>
                <span data-testid="invoice-total-display">{baseTotal.toFixed(2)}</span>
              </div>
              {inv.due_date && (
                <div className="text-xs text-[#71717A] mt-3 uppercase tracking-[0.2em]">
                  Due {inv.due_date}
                </div>
              )}
            </div>
            {/* Reservation number(s) — right column, mono */}
            {(inv.trips || []).filter(t => t.confirmation_code).length > 0 && (
              <div
                data-testid="reservation-numbers"
                className="text-right"
              >
                <div className="overline mb-1">{inv.trips.length === 1 ? "Reservation" : "Reservations"}</div>
                <div className="font-mono text-sm text-[#050505] space-y-0.5">
                  {inv.trips.map((t, i) => t.confirmation_code && (
                    <div key={i}>{t.confirmation_code}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {done === "pending" && (
          <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 mb-8 text-sm text-[#F5F2EB]" data-testid="pending-banner">
            <b className="text-[#D4AF37]">Thanks — we have your payment notice.</b> Dispatch will verify and mark this invoice as paid shortly.
          </div>
        )}

        {/* One-tap repeat-customer chip — shown when a card-on-file exists for this email */}
        {done !== "pending" && savedCards.length > 0 && (
          <div className="mb-6" data-testid="saved-card-chip">
            <button
              type="button"
              disabled={busy}
              data-testid="saved-card-pay"
              onClick={() => chargeSavedCard(savedCards[0].saved_card_id)}
              className="w-full border-2 border-[#004EEB] bg-[#E5F0FF] hover:bg-[#004EEB] hover:text-white group transition-all duration-300 p-5 flex items-center justify-between disabled:opacity-50"
            >
              <div className="flex items-center gap-4">
                <CardIcon size={22} className="text-[#004EEB] group-hover:text-white shrink-0"/>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold">⚡ One-tap pay · Saved on this device</div>
                  <div className="text-base font-[Outfit] font-medium mt-0.5">
                    {savedCards[0].brand || "Card"} ···· {savedCards[0].last4 || "••••"} — Pay ${amount}
                  </div>
                </div>
              </div>
              <ArrowRight size={20} className="text-[#004EEB] group-hover:text-white"/>
            </button>
            {savedCards.length > 1 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="other-saved-cards">
                {savedCards.slice(1).map(c => (
                  <button key={c.saved_card_id}
                    disabled={busy}
                    onClick={() => chargeSavedCard(c.saved_card_id)}
                    className="text-[10px] uppercase tracking-[0.16em] text-[#A1A1AA] border border-white/10 px-3 py-1.5 hover:border-[#004EEB] hover:text-[#004EEB] disabled:opacity-50">
                    or {c.brand || "Card"} ···· {c.last4 || "••••"}
                  </button>
                ))}
              </div>
            )}
            <div className="relative flex items-center mt-6">
              <div className="flex-1 h-px bg-white/10"/>
              <span className="px-3 text-[9px] uppercase tracking-[0.22em] text-[#71717A] font-bold">or choose another method</span>
              <div className="flex-1 h-px bg-white/10"/>
            </div>
          </div>
        )}

        {/* Method picker */}
        {done !== "pending" && (
        <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MethodBtn id="card"  picked={picked} onPick={setPicked} icon={<CardIcon size={18}/>}    label="Card"
            disabled={!inv.payment_methods.card.enabled}/>
          {inv.payment_methods.zelle?.enabled !== false && (
            <MethodBtn id="zelle" picked={picked} onPick={setPicked} icon={<Smartphone size={18}/>} label="Zelle"/>
          )}
          {inv.payment_methods.venmo?.enabled !== false && (
            <MethodBtn id="venmo" picked={picked} onPick={setPicked} icon={<Smartphone size={18}/>} label="Venmo"/>
          )}
          {inv.payment_methods.cash?.enabled !== false && (
            <MethodBtn id="cash"  picked={picked} onPick={setPicked} icon={<Banknote size={18}/>}   label="Cash"/>
          )}
        </div>

        {picked === "card" && inv.payment_methods.card.enabled && (
          <div className="border border-white/10 bg-[#0A0A0A] p-5 mb-8" data-testid="card-pay-panel">
            <div className="text-[11px] text-[#D4AF37] mb-4 flex items-center gap-2 uppercase tracking-[0.18em]">
              <ShieldCheck size={14}/>
              One-tap pay · Apple Pay · Google Pay · Card
            </div>
            <PaymentForm
              applicationId={inv.payment_methods.card.app_id}
              locationId={inv.payment_methods.card.location_id}
              cardTokenizeResponseReceived={handleSquareToken}
              createPaymentRequest={() => ({
                countryCode: "US",
                currencyCode: "USD",
                total: { amount, label: `Delux Limo · Invoice ${inv.invoice_number}` },
              })}
            >
              <div className="space-y-3" data-testid="square-buttons">
                {/* Apple Pay / Google Pay surface first — they render only when the
                    device + browser actually support them, so non-mobile users still
                    fall through to the Credit Card form below. */}
                <div data-testid="apple-pay-wrap"><ApplePay/></div>
                <div data-testid="google-pay-wrap"><GooglePay/></div>
                <div className="relative flex items-center py-1">
                  <div className="flex-1 h-px bg-white/10"/>
                  <span className="px-3 text-[9px] uppercase tracking-[0.22em] text-[#71717A]">or pay by card</span>
                  <div className="flex-1 h-px bg-white/10"/>
                </div>
                <CreditCard
                  buttonProps={{
                    css: {
                      backgroundColor: "#050505",
                      color: "#FFFFFF",
                      fontFamily: "Outfit, sans-serif",
                      fontWeight: 600,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      fontSize: "12px",
                      borderRadius: "0",
                      "&:hover": { backgroundColor: "#004EEB" },
                    },
                  }}
                >
                  {busy ? "Processing…" : `Pay $${amount}`}
                </CreditCard>
                {inv.customer_email && savedCards.length === 0 && (
                  <div className="pt-2 text-[11px] text-[#A1A1AA] leading-snug" data-testid="save-card-auto-note">
                    <span className="text-[#D4AF37]">Your card is saved automatically</span> as a Payment Method for one-tap pay on future invoices and bookings. Card details stay with Square — we only see brand + last 4.
                  </div>
                )}
              </div>
            </PaymentForm>
          </div>
        )}

        {picked === "card" && !inv.payment_methods.card.enabled && (
          <div className="border border-white/10 bg-[#0A0A0A] p-5 mb-8 text-sm text-[#A1A1AA]">
            Card payments aren&apos;t enabled for this invoice. Please choose Zelle, Venmo, or Cash.
          </div>
        )}

        {picked === "zelle" && (
          <ManualBlock title="Zelle"
            handle={inv.payment_methods.zelle.handle || "(414) 333-6275"}
            instructions="Send via your bank's Zelle app, then enter the confirmation number below so we can match it."
            memoHint={`Include invoice ${inv.invoice_number} in the memo`}
            onConfirm={(ref) => handleManual("zelle", ref)}
            busy={busy}
            amount={amount}/>
        )}
        {picked === "venmo" && (
          <ManualBlock title="Venmo"
            handle={inv.payment_methods.venmo.handle || "@deluxlimotransgroup"}
            instructions="Open Venmo and send to the handle below, then enter your Venmo confirmation."
            memoHint={`Include invoice ${inv.invoice_number} in the note`}
            onConfirm={(ref) => handleManual("venmo", ref)}
            busy={busy}
            amount={amount}/>
        )}
        {picked === "cash" && (
          <div className="border border-white/10 bg-[#0A0A0A] p-5 mb-8 space-y-4">
            <div className="text-sm text-[#F5F2EB]">Pay cash to your dispatcher in person.</div>
            <div className="text-xs text-[#71717A]">Total due: <span className="text-[#D4AF37]">${amount}</span></div>
            <button
              data-testid="confirm-cash-pay"
              disabled={busy}
              onClick={() => handleManual("cash")}
              className="btn-gold w-full"
            >I&apos;ll bring cash — notify dispatch</button>
          </div>
        )}
        </>
        )}

        <InvoiceSummary inv={inv}/>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="public-light min-h-screen bg-[#050505] text-[#F5F2EB]" data-testid="pay-invoice-shell">
      <header className="border-b border-white/10 py-6 px-6 flex items-center justify-between max-w-5xl mx-auto">
        <div>
          <div className="font-[Outfit] tracking-[0.3em] text-sm font-bold">DELUX LIMO</div>
          <div className="text-[10px] text-[#71717A] tracking-[0.22em] uppercase mt-0.5">Trans Group · Milwaukee</div>
        </div>
        <a href="tel:+14143336275" className="text-[11px] font-mono tracking-wider text-[#71717A] hover:text-[#004EEB] hidden md:inline-flex">(414) 333-6275</a>
      </header>
      {children}
      <footer className="border-t border-white/10 py-6 text-center text-[10px] text-[#71717A] tracking-[0.18em] uppercase">
        Questions? Call (414) 333-6275 · deluxlimotransgroup.com
      </footer>
    </div>
  );
}

function PaidBanner({ inv, token }) {
  return (
    <div className="max-w-2xl mx-auto px-6 mt-12 text-center" data-testid="paid-banner">
      <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-6">
        <Check size={14}/> Paid
      </div>
      <h1 className="font-serif text-4xl text-[#D4AF37]">Thank you</h1>
      <p className="text-sm text-[#A1A1AA] mt-2">
        Invoice {inv.invoice_number} was paid {inv.paid_date ? `on ${inv.paid_date}` : "successfully"}{inv.payment_method ? ` via ${inv.payment_method}` : ""}.
      </p>
      <a data-testid="download-receipt-btn"
        href={`${API}/invoices/consolidated/public/${token}/pdf`}
        target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-[#D4AF37] text-[#0a0a0a] text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-[#c9a230] transition-colors">
        <Download size={13}/> Download receipt (PDF)
      </a>
    </div>
  );
}

function InvoiceSummary({ inv }) {
  const customItems = (!inv.trips || inv.trips.length === 0) && (inv.items || []).length > 0 ? inv.items : null;
  const tdList = customItems ? normalizeTripDetails(inv.trip_details) : [];
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 pb-16">
      {tdList.length > 0 && (
        <div className="mb-8">
          <div className="overline text-[#A1A1AA] mb-3">Trip route</div>
          <div className="space-y-3">
            {tdList.map((td, ti) => (
              <div key={ti} className="border border-white/10 bg-[#0A0A0A] p-4 space-y-2 text-sm" data-testid={`trip-route-${ti}`}>
                {tdList.length > 1 && (
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">{td.label || `Trip ${ti + 1}`}</div>
                )}
                {td.trip_type && (
                  <div className="text-[#F5F2EB]" data-testid={`trip-${ti}-type`}><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Type</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-0.5">{td.trip_type === "hourly" ? "Hourly Charter" : "Point to Point"}</span>
                    {td.trip_type === "hourly" && td.hours ? <span className="ml-2 text-[#A1A1AA] text-xs">{td.hours} hour{td.hours === 1 ? "" : "s"}</span> : null}
                  </div>
                )}
                {td.booked_on && (
                  <div className="text-[#A1A1AA] text-xs"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Booked</span>{fmtBookedOn(td.booked_on)}</div>
                )}
                {td.pickup_datetime && (
                  <div className="text-[#A1A1AA] text-xs"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">When</span>{td.pickup_datetime.replace("T", " · ")}</div>
                )}
                {td.pickup_address && (
                  <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">{td.trip_type === "hourly" ? "Starting at" : "Pickup"}</span>{td.pickup_address}</div>
                )}
                {(td.stops || []).map((st, i) => (
                  <div key={i} className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] inline-block w-20">Stop {i + 1}</span>{st.time ? `${fmtStopTime(st.time)} — ` : ""}{st.address}</div>
                ))}
                {td.dropoff_address && (
                  <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Drop-off</span>{td.dropoff_address}</div>
                )}
                {td.flight && (
                  <div className="text-[#F5F2EB]" data-testid={`trip-${ti}-flight`}><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Flight</span><FlightSummary flight={td.flight}/></div>
                )}
                {td.passengers > 0 && (
                  <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Passengers</span>{td.passengers}</div>
                )}
                {(td.chauffeurs || []).map((c, ci) => (
                  <div key={ci} className="text-[#F5F2EB]" data-testid={`trip-${ti}-chauffeur-${ci}`}>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">{(td.chauffeurs || []).length > 1 ? `Chauffeur ${ci + 1}` : "Chauffeur"}</span>
                    {[c.name, c.phone, c.vehicle].filter(Boolean).join(" · ")}
                  </div>
                ))}
                {tdList.length > 1 && td.amount != null && (
                  <div className="text-[#D4AF37] pt-1" data-testid={`trip-${ti}-amount`}><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Amount</span>${Number(td.amount).toFixed(2)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {inv.chauffeur && (inv.chauffeur.name || inv.chauffeur.phone || inv.chauffeur.vehicle) && (
        <div className="mb-8">
          <div className="overline text-[#A1A1AA] mb-3">Your chauffeur</div>
          <div className="border border-white/10 bg-[#0A0A0A] p-4 space-y-2 text-sm" data-testid="chauffeur-info">
            {inv.chauffeur.name && (
              <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Chauffeur</span>{inv.chauffeur.name}</div>
            )}
            {inv.chauffeur.phone && (
              <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Phone</span><a href={`tel:${inv.chauffeur.phone}`} className="text-[#D4AF37] hover:underline">{inv.chauffeur.phone}</a></div>
            )}
            {inv.chauffeur.vehicle && (
              <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-20">Vehicle</span>{inv.chauffeur.vehicle}</div>
            )}
          </div>
        </div>
      )}
      <div className="overline text-[#A1A1AA] mb-3">{customItems ? "Charges on this invoice" : "Trips on this invoice"}</div>
      {customItems ? (
        <div className="border border-white/10 bg-[#0A0A0A]" data-testid="item-list">
          <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-[0.22em] text-[#71717A] border-b border-white/5">
            <div className="col-span-9">Description</div>
            <div className="col-span-3 text-right">Amount</div>
          </div>
          {customItems.map((it, i) => (
            <div key={i} className="grid grid-cols-12 px-4 py-4 border-b border-white/5 last:border-b-0 items-center text-sm" data-testid="item-row">
              <div className="col-span-9 text-[#F5F2EB] text-[13px]">{it.description}</div>
              <div className="col-span-3 text-right text-[#D4AF37] font-medium">${Number(it.amount || 0).toFixed(2)}</div>
            </div>
          ))}
        </div>
      ) : (
      <div className="border border-white/10 bg-[#0A0A0A]" data-testid="trip-list">
        <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-[0.22em] text-[#71717A] border-b border-white/5">
          <div className="col-span-3">Pickup Date / Time</div>
          <div className="col-span-2">Booked</div>
          <div className="col-span-5">Route</div>
          <div className="col-span-2 text-right">Fare</div>
        </div>
        {inv.trips.map((t, i) => (
          <div key={t.booking_id || i} className="grid grid-cols-12 px-4 py-4 border-b border-white/5 last:border-b-0 items-center text-sm" data-testid="trip-row">
            <div className="col-span-3 text-[#F5F2EB] text-xs">
              {fmtDateTime(t.pickup_datetime)}
              {t.confirmation_code && (
                <div className="text-[10px] text-[#D4AF37] mt-1 tracking-[0.14em]">#{t.confirmation_code}</div>
              )}
            </div>
            <div className="col-span-2 text-[#A1A1AA] text-[11px]">{t.created_at ? fmtDateTime(t.created_at) : "—"}</div>
            <div className="col-span-5 text-[13px] min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[#F5F2EB]">{shortAddr(t.pickup_address)}</span>
                {(t.stops || []).map((s, si) => (
                  <React.Fragment key={si}><ArrowRight size={12} className="text-[#D4AF37] shrink-0"/><span className="text-[#A1A1AA]">{shortAddr(s)}</span></React.Fragment>
                ))}
                {t.trip_type === "hourly"
                  ? <span className="text-[#A1A1AA]">· Hourly charter{t.hours ? ` · ${t.hours} hr` : ""}</span>
                  : <><ArrowRight size={12} className="text-[#D4AF37] shrink-0"/><span className="text-[#F5F2EB]">{shortAddr(t.dropoff_address)}</span></>}
              </div>
              {(t.vehicle_name || t.assigned_driver_name) && (
                <div className="text-[10px] text-[#71717A] mt-0.5">{[t.vehicle_name, t.assigned_driver_name].filter(Boolean).join(" · ")}</div>
              )}
            </div>
            <div className="col-span-2 text-right text-[#D4AF37] font-medium">${Number(t.fare || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>
      )}
      <div className="mt-6 ml-auto max-w-xs text-sm">
        <Row k="Subtotal" v={`$${Number(inv.subtotal).toFixed(2)}`}/>
        {Number(inv.discount) > 0 && <Row k="Discount" v={`−$${Number(inv.discount).toFixed(2)}`} accent/>}
        {Number(inv.tax_pct) > 0  && <Row k={`Tax (${Number(inv.tax_pct).toFixed(2)}%)`} v={`$${Number(inv.tax).toFixed(2)}`}/>}
        {Number(inv.gratuity) > 0 && <Row k="Gratuity" v={`$${Number(inv.gratuity).toFixed(2)}`}/>}
        <div className="border-t border-[#D4AF37]/40 mt-2 pt-2 flex justify-between">
          <span className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA]">Total</span>
          <span className="text-xl text-[#D4AF37] font-semibold">${Number(inv.total).toFixed(2)}</span>
        </div>
      </div>

      {inv.notes && (
        <div className="mt-8 border border-white/10 bg-[#0A0A0A] p-4 text-xs text-[#A1A1AA]" data-testid="invoice-notes">
          <div className="overline text-[#71717A] mb-1">Notes</div>
          {inv.notes}
        </div>
      )}
    </div>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-[#A1A1AA] text-xs">{k}</span>
      <span className={accent ? "text-[#D4AF37]" : "text-[#F5F2EB]"}>{v}</span>
    </div>
  );
}

function MethodBtn({ id, picked, onPick, icon, label, disabled }) {
  const active = picked === id;
  return (
    <button
      data-testid={`pay-method-${id}`}
      disabled={disabled}
      onClick={() => onPick(id)}
      className={`flex flex-col items-center gap-2 py-3 px-3 border text-xs uppercase tracking-[0.16em] transition
        ${disabled ? "opacity-40 cursor-not-allowed border-white/5 text-[#71717A]" :
          active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" :
                   "border-white/10 text-[#A1A1AA] hover:border-white/30"}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function ManualBlock({ title, handle, instructions, memoHint, onConfirm, busy, amount }) {
  const [reference, setReference] = useState("");
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5 mb-8 space-y-4" data-testid={`manual-${title.toLowerCase()}`}>
      <div>
        <div className="overline mb-2">{title} payment</div>
        <div className="text-xs text-[#A1A1AA]">{instructions}</div>
      </div>
      <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">Send {`$${amount}`} to</div>
        <div className="font-serif text-2xl text-[#D4AF37] mt-1">{handle}</div>
        <div className="text-xs text-[#71717A] mt-2">{memoHint}</div>
      </div>
      <div>
        <label className="field-label">Confirmation # (optional)</label>
        <input
          data-testid="manual-pay-ref"
          className="field-input"
          value={reference}
          onChange={e => setReference(e.target.value)}
          placeholder={`e.g. ${title} confirmation`}
        />
      </div>
      <button
        data-testid="confirm-manual-pay"
        disabled={busy}
        onClick={() => onConfirm(reference || null)}
        className="btn-gold w-full"
      >I&apos;ve sent the payment</button>
      <p className="text-[10px] text-[#71717A] uppercase tracking-[0.18em]">Dispatch will verify receipt before marking the invoice paid.</p>
    </div>
  );
}
